import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Typography, CircularProgress, Box, styled, Tooltip, Skeleton } from '@mui/material';
import PropTypes from 'prop-types';

import { send_request } from '../../utils/Request';
import { fetchUsageData } from '../../utils/UsageAPI';
import { useRegisterRefreshCallback } from '../../contexts/UsageRefreshContext';
import BoltIcon from '@mui/icons-material/Bolt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import useProjectStore from '../ProjectCanvas/useProjectStore';

const REFRESH_INTERVALS = {
  ACTIVE: 30000,
  BACKGROUND: 60000,
  AFTER_ACTION: 2000,
};

const COLORS = {
  actions: '#2196f3',
  aiCredits: '#7B1FA2',
  background: '#e0e0e0',
  warning: '#FFA722',
  danger: '#f44336',
};

const THRESHOLDS = {
  WARNING: 70,
  CRITICAL: 90,
};

const ICON_STYLE = {
  fontSize: '14px',
  width: '14px',
  height: '14px',
};

const SKELETON_STYLES = {
  borderRadius: '8px',
};

const UsageContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'isOpen',
})(({ theme, isOpen }) => ({
  width: 'calc(100% - 16px)',
  padding: isOpen ? '12px 16px' : '4px 8px',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  border: isOpen ? `1px solid ${theme.palette.divider}` : 'none',
  margin: '16px 8px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: isOpen ? '12px' : '16px',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: isOpen ? theme.palette.action.hover : theme.palette.background.paper,
    transform: isOpen ? 'translateY(-1px)' : 'none',
    boxShadow: isOpen ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
    borderColor: isOpen ? theme.palette.divider : 'transparent',
    '& .MuiSvgIcon-root': {
      color: `${theme.palette.text.primary} !important`,
    },
  },
  '&:active': {
    transform: isOpen ? 'translateY(0px)' : 'none',
    transition: 'all 0.1s ease',
  },
}));

const HeaderRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'isOpen',
})(({ theme, isOpen }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  marginTop: isOpen ? '4px' : '0',
  paddingTop: isOpen ? '4px' : '0',
  borderTop: isOpen ? `1px solid ${theme.palette.action.hover}` : 'none',
}));

const DonutContainer = styled(Box)({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
});

const DonutText = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  fontSize: '10px',
  fontWeight: 'bold',
  textAlign: 'center',
  lineHeight: '1',
  pointerEvents: 'none',
  '& .MuiSvgIcon-root': {
    color: theme.palette.text.secondary,
  },
}));

const UsageRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'isOpen',
})(({ isOpen }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: isOpen ? '12px' : '8px',
  width: '100%',
  justifyContent: isOpen ? 'flex-start' : 'center',
}));

const UsageDetails = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  minWidth: 0,
  flex: 1,
});

const UsageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  lineHeight: '1.2',
}));

const UsageSubtext = styled(Typography)(({ theme }) => ({
  fontSize: '10px',
  color: theme.palette.text.secondary,
  lineHeight: '1.2',
}));

const formatNumber = num => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const getProgressColor = (used, total, type) => {
  if (total === 0) return COLORS.background;
  const percentage = (used / total) * 100;
  if (percentage >= THRESHOLDS.CRITICAL) return COLORS.danger;
  if (percentage >= THRESHOLDS.WARNING) return COLORS.warning;
  return type === 'aiCredits' ? COLORS.aiCredits : COLORS.actions;
};

const getProgressValue = (used, total) => {
  if (total === 0) return 0;
  return Math.min((used / total) * 100, 100);
};

const DonutChart = React.memo(({ used, total, color, icon, title, type, isOpen }) => {
  const progressValue = useMemo(() => getProgressValue(used, total), [used, total]);
  const progressColor = useMemo(() => getProgressColor(used, total, type), [used, total, type]);

  const renderIcon = () => {
    if (type === 'actions') {
      return <BoltIcon style={ICON_STYLE} />;
    } else if (type === 'aiCredits') {
      return <AutoAwesomeIcon style={ICON_STYLE} />;
    }
    return null;
  };

  const content = (
    <UsageRow isOpen={isOpen}>
      <DonutContainer>
        <CircularProgress
          variant="determinate"
          value={100}
          size={32}
          thickness={4}
          sx={{
            color: COLORS.background,
            position: 'absolute',
          }}
        />
        <CircularProgress
          variant="determinate"
          value={progressValue}
          size={32}
          thickness={4}
          sx={{
            color: progressColor,
            position: 'absolute',
          }}
        />
        <DonutText>{renderIcon()}</DonutText>
      </DonutContainer>

      {isOpen && (
        <UsageDetails>
          <UsageTitle>{title}</UsageTitle>
          <UsageSubtext>{formatNumber(total - used)} remaining</UsageSubtext>
        </UsageDetails>
      )}
    </UsageRow>
  );

  if (isOpen) {
    return content;
  }

  return (
    <Tooltip title={`${title}: ${formatNumber(used)}/${formatNumber(total)}`} placement="right">
      {content}
    </Tooltip>
  );
});

DonutChart.propTypes = {
  used: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
  icon: PropTypes.element.isRequired,
  title: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['actions', 'aiCredits']).isRequired,
  isOpen: PropTypes.bool.isRequired,
};

const SidebarUsageDisplay = ({ isOpen }) => {
  const [usageData, setUsageData] = useState({
    actions: { used: 0, total: 0 },
    aiCredits: { used: 0, total: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [isV2Billing, setIsV2Billing] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const isMountedRef = useRef(true);

  const handleContainerClick = useCallback(() => {
    window.open('/organization', '_blank');
  }, []);

  /** Read billing version from ProjectStore (set by InternalApp) instead of re-fetching */
  const determineBillingVersion = useCallback(async () => {
    // Wait for InternalApp to finish billing initialization
    if (!useProjectStore.getState().billingInitialized) {
      await new Promise(resolve => {
        const interval = setInterval(() => {
          if (useProjectStore.getState().billingInitialized) {
            clearInterval(interval);
            resolve();
          }
        }, 50);
      });
    }
    const billingVersion = useProjectStore.getState().isV2Billing ?? false;
    setIsV2Billing(billingVersion);
    return billingVersion;
  }, []);

  const fetchV2BillingData = useCallback(async () => {
    try {
      const usageResponse = await fetchUsageData();

      if (usageResponse.success && usageResponse.data) {
        const { usedActions, totalActions, usedAiCredits, totalAiCredits } = usageResponse.data;

        setUsageData({
          actions: {
            used: usedActions || 0,
            total: totalActions || 0,
          },
          aiCredits: {
            used: usedAiCredits || 0,
            total: totalAiCredits || 0,
          },
        });
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching V2 billing data:', error);
      await fetchLegacyBillingData();
    }
  }, []);

  const fetchLegacyBillingData = useCallback(async () => {
    try {
      const sessionData = await send_request(
        'authz-service/clientstats/get-client-session-data',
        '',
        '',
        'get'
      );

      const actions = {
        used: sessionData?.data?.currentComponentSessionRun || 0,
        total: sessionData?.data?.maxComponentSession || 0,
      };

      const aiCredits = {
        used: sessionData?.data?.aiCreditsUsed || 0,
        total: sessionData?.data?.aiCreditsTotal || 0,
      };

      setUsageData({ actions, aiCredits });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching legacy billing data:', error);
      setLoading(false);
    }
  }, []);

  const refreshUsageData = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      // Use cached billing version from state instead of re-fetching
      if (isV2Billing) {
        await fetchV2BillingData();
      } else {
        await fetchLegacyBillingData();
      }

      if (isMountedRef.current) {
        setLastUpdated(new Date());
        setHasError(false);
      }
    } catch (error) {
      console.error('Error refreshing usage data:', error);
      if (isMountedRef.current) {
        setHasError(true);
      }
    }
  }, [isV2Billing, fetchV2BillingData, fetchLegacyBillingData]);

  useEffect(() => {
    isMountedRef.current = true;

    const initializeUsageData = async () => {
      try {
        const billingVersion = await determineBillingVersion();

        if (billingVersion) {
          await fetchV2BillingData();
        } else {
          await fetchLegacyBillingData();
        }

        if (isMountedRef.current) {
          setLastUpdated(new Date());
        }
      } catch (error) {
        console.error('Error initializing usage data:', error);
        if (isMountedRef.current) {
          setHasError(true);
          setLoading(false);
        }
      }
    };

    initializeUsageData();

    return () => {
      isMountedRef.current = false;
    };
  }, [determineBillingVersion, fetchV2BillingData, fetchLegacyBillingData]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isMountedRef.current) {
        refreshUsageData();
      }
    }, REFRESH_INTERVALS.ACTIVE);

    return () => clearInterval(interval);
  }, [refreshUsageData]);

  useRegisterRefreshCallback(refreshUsageData);

  const loadingSkeleton = useMemo(
    () => (
      <UsageContainer isOpen={isOpen}>
        <UsageRow isOpen={isOpen}>
          <Skeleton variant="rectangular" width={32} height={32} sx={SKELETON_STYLES} />
          {isOpen && (
            <Box sx={{ flex: 1, ml: 1 }}>
              <Skeleton variant="text" width="60%" height={14} />
              <Skeleton variant="text" width="40%" height={12} />
            </Box>
          )}
        </UsageRow>
        <UsageRow isOpen={isOpen}>
          <Skeleton variant="rectangular" width={32} height={32} sx={SKELETON_STYLES} />
          {isOpen && (
            <Box sx={{ flex: 1, ml: 1 }}>
              <Skeleton variant="text" width="60%" height={14} />
              <Skeleton variant="text" width="40%" height={12} />
            </Box>
          )}
        </UsageRow>
      </UsageContainer>
    ),
    [isOpen]
  );

  if (loading) {
    return loadingSkeleton;
  }

  if (hasError) {
    return null;
  }

  return (
    <UsageContainer isOpen={isOpen} onClick={handleContainerClick}>
      <DonutChart
        used={usageData.actions.used}
        total={usageData.actions.total}
        color={COLORS.actions}
        icon={<BoltIcon />}
        title="Actions"
        type="actions"
        isOpen={isOpen}
      />

      {usageData.aiCredits.total > 0 && (
        <DonutChart
          used={usageData.aiCredits.used}
          total={usageData.aiCredits.total}
          color={COLORS.aiCredits}
          icon={<AutoAwesomeIcon />}
          title="AI Credits"
          type="aiCredits"
          isOpen={isOpen}
        />
      )}

      {isOpen && (
        <HeaderRow isOpen={isOpen}>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '9px' }}>
            {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : 'Loading...'}
          </Typography>
        </HeaderRow>
      )}
    </UsageContainer>
  );
};

SidebarUsageDisplay.propTypes = {
  isOpen: PropTypes.bool.isRequired,
};

export default SidebarUsageDisplay;
